import { Component, OnInit } from '@angular/core';
import { AppConstants } from "../../constants/app-constants";
import { NavigationEnd, Router } from "@angular/router";
import { environment } from 'src/environments/environment';
import { UserService } from 'src/app/services/user/user.service';
import { User } from 'src/app/model/user';
import { Subscription } from 'rxjs';

/**
 * Interface for navigation menu items
 */
export interface NavigationMenuItem {
  url: string;
  icon: string;
  permission: string;
  children?: NavigationMenuItem[];
  expanded?: boolean;
}

/**
 * Navigation component handles the left sidebar logic
 */
@Component({
  selector: 'app-navigation',
  templateUrl: './navigation.component.html',
  styleUrls: ['./navigation.component.scss']
})
export class NavigationComponent {
  /** Available menus to render */
  availableMenus: NavigationMenuItem[] = [
    {
      url: "Mac Program",
      icon: "local_shipping",
      permission: "",
      children: [
        {
          url: AppConstants.MENU_APPLICATIONS,
          icon: "description",
          permission: "APPLICATIONS_ACCESS",
        },
        {
          url: AppConstants.MENU_SUPPLIERS,
          icon: "store_mall_directory",
          permission: "SUPPLIERS_ACCESS",
        },
        {
          url: AppConstants.MENU_TRANSACTIONS,
          icon: "receipt",
          permission: "TRANSACTIONS_ACCESS",
        },
        {
          url: AppConstants.MENU_TERMINATIONS,
          icon: "person_remove",
          permission: "TERMINATION_ACCESS",
        },
        {
          url: AppConstants.MENU_MANUAL_REVIEW,
          icon: "gavel",
          permission: "MYSTIC_ACCESS",
        }
      ],
    },
    {
      url: 'PLWC Program',
      icon: "shopping_cart",
      permission: '',
      children: [
        {
          url: AppConstants.MENU_CREDIT_PLANS,
          icon: "credit_card",
          permission: "CREDIT_PLANS_ACCESS",
        },
        {
          url: AppConstants.MENU_BEYOND_SUPPLIERS,
          icon: "store_mall_directory",
          permission: "SUPPLIERS_ACCESS",
        }
      ]
    },
    {
      url: AppConstants.MENU_USERS,
      icon: "person",
      permission: "USERS_ACCESS",
    },
  ];

  /** Menus being rendered */
  menus: NavigationMenuItem[];

  /** The current URL of the application */
  currentUrl: string;

  /** Subscription reference for cleanup */
  private sub: Subscription;

  /**
   * Constructor to initalize services and subscribe to url changes
   * 
   * @param router
   */
  constructor(
    private _router: Router,
    private _userService: UserService,
  ) {
    // Set the initial url
    this.currentUrl = this._router.url.replace('/', '');

    // Subscribe to URL changes and update the header title accordingly
    this._router.events.subscribe(
      (event: any): void => {
        if (event instanceof NavigationEnd) {
          this.currentUrl = event.url.replace('/', '');
        }
      });

    // Set the initial permissions
    const permissions = this._userService.user?.permissions.map(x => x.permission) || [];
    this.menus = this.filterMenusByPermissions(this.availableMenus, permissions);

    // Subscribe to future user changes to update permissions
    this.sub = this._userService.user$.subscribe(
      (user: User | null): void => {
        const permissions = user?.permissions.map(x => x.permission) || [];
        this.menus = this.filterMenusByPermissions(this.availableMenus, permissions);
      });
  }

  /**
   * Unsubscribe from all subscriptions on component destroy
   */
  ngOnDestroy(): void {
    if (this.sub) {
      this.sub.unsubscribe();
    }
  }

  /**
   * Filters menu items based on user permissions, including nested children
   * @param menus The menu items to filter
   * @param permissions The user's permissions
   * @returns Filtered menu items
   */
  private filterMenusByPermissions(menus: NavigationMenuItem[], permissions: string[]): NavigationMenuItem[] {
    if (permissions == undefined || permissions.length === 0) return [];

    return menus.filter(menu => {
      // Check if user has permission for this menu item
      const hasPermission = !menu.permission || permissions.indexOf(menu.permission) !== -1;

      if (hasPermission && menu.children) {
        // If menu has children, recursively filter them
        menu.children = this.filterMenusByPermissions(menu.children, permissions);
      }

      return hasPermission;
    });
  }

  /**
   * Toggles the expanded state of a menu item
   * @param menu The menu item to toggle
   */
  toggleMenu(menu: NavigationMenuItem): void {
    if (menu.children && menu.children.length > 0) {
      menu.expanded = !menu.expanded;
    }
  }

  /**
   * Checks if a menu item or any of its children is currently active
   * @param menu The menu item to check
   * @returns True if the menu or any child is active
   */
  isMenuActive(menu: NavigationMenuItem): boolean {
    if (this.currentUrl === menu.url) {
      return true;
    }

    if (menu.children) {
      return menu.children.some(child => this.isMenuActive(child));
    }

    return false;
  }
}
